// src/pages/Community.tsx
import React, { useState, useEffect } from 'react';
import { 
  Users, 
  FileText, 
  Megaphone, 
  Calendar,
  Download,
  MapPin,
  MessageSquare,
  Share2,
  Filter,
  Search,
  Award,
  Target,
  TrendingUp,
  Eye,
  User
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import ManifestoCard, { Manifesto } from '../components/ManifestoCard';

// Define types for better TypeScript support
interface Citizen {
  id: number;
  phoneNumber: string;
  fullName?: string;
  email?: string;
  wardId?: number;
  wardName?: string;
  constituencyId?: number;
  constituencyName?: string;
  [key: string]: any;
}

interface Campaign {
  id: number;
  campaignName: string;
  description?: string;
  type: any;
  content: string;
  summary?: string;
  aspirantId: number;
  aspirantName: string;
  targetWardIds?: number[];
  publishedAt?: string;
  createdAt: string;
  documentUrl?: string;
  videoUrl?: string;
  imageUrl?: string;
  feedbackLink?: string;
  smsRecipients?: number;
  status?: string;
  [key: string]: any;
}

interface Memorandum {
  id: number;
  memorandumNumber: string;
  subject: string;
  content: string;
  type: any;
  aspirantId: number;
  aspirantName: string;
  recipientDepartment: string;
  evidenceSummary?: string;
  estimatedCost?: number;
  generatedAt: string;
  downloadUrl?: string;
  [key: string]: any;
}

interface Aspirant {
  id: number;
  name: string;
  phoneNumber: string;
  email?: string;
  position?: any;
  wardId?: number;
  wardName?: string;
  constituencyId?: number;
  constituencyName?: string;
  party?: string;
  createdAt?: string;
  [key: string]: any;
}

const Community: React.FC = () => {
  const navigate = useNavigate();
  const { citizen } = useAuth();
  const [activeTab, setActiveTab] = useState<'campaigns' | 'manifestos' | 'memorandums' | 'aspirants'>('campaigns');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [manifestos, setManifestos] = useState<Manifesto[]>([]);
  const [memorandums, setMemorandums] = useState<Memorandum[]>([]);
  const [aspirants, setAspirants] = useState<Aspirant[]>([]);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  // Get citizen's ward ID from profile - safely access properties
  const typedCitizen = citizen as Citizen | null;
  const citizenWardId = typedCitizen?.wardId;

  useEffect(() => {
    if (citizenWardId) {
      fetchCommunityData();
    } else {
      setError('Please update your profile with your ward information to see community content.');
      setLoading(false);
    }
  }, [citizenWardId]);

  const fetchCommunityData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!citizenWardId) {
        setError('Please update your profile with your ward information to see community content.');
        setLoading(false);
        return;
      }

      console.log('📢 Fetching community data for ward:', citizenWardId);
      
      // Fetch campaigns for citizen's ward - with better error handling
      try {
        console.log('📢 Fetching campaigns for ward:', citizenWardId);
        const campaignsResponse = await apiService.getPublishedCampaignsByWard(citizenWardId);
        console.log('📢 Campaigns response:', campaignsResponse);
        
        // Handle different response structures
        if (campaignsResponse.data) {
          if (campaignsResponse.data.success && Array.isArray(campaignsResponse.data.data)) {
            setCampaigns(campaignsResponse.data.data);
            console.log(`✅ Found ${campaignsResponse.data.data.length} campaigns`);
          } else if (Array.isArray(campaignsResponse.data)) {
            setCampaigns(campaignsResponse.data);
            console.log(`✅ Found ${campaignsResponse.data.length} campaigns`);
          } else if (campaignsResponse.data.data && Array.isArray(campaignsResponse.data.data)) {
            setCampaigns(campaignsResponse.data.data);
            console.log(`✅ Found ${campaignsResponse.data.data.length} campaigns`);
          } else {
            console.log('⚠️ Unexpected campaigns response format:', campaignsResponse.data);
            setCampaigns([]);
          }
        } else {
          setCampaigns([]);
        }
      } catch (campaignError) {
        console.error('❌ Error fetching campaigns:', campaignError);
        // Don't set main error - just log it and continue with empty campaigns
        setCampaigns([]);
      }

      // Fetch aspirants in citizen's ward
      try {
        console.log('📢 Fetching aspirants for ward:', citizenWardId);
        const aspirantsResponse = await apiService.getAspirantsByWard(citizenWardId);
        console.log('📢 Aspirants response:', aspirantsResponse);
        
        if (aspirantsResponse.data) {
          if (aspirantsResponse.data.success && Array.isArray(aspirantsResponse.data.data)) {
            const aspirantsData = aspirantsResponse.data.data;
            setAspirants(aspirantsData);
            console.log(`✅ Found ${aspirantsData.length} aspirants`);
            
            // If there are aspirants, fetch their memorandums
            if (aspirantsData.length > 0) {
              const aspirantIds = aspirantsData.map((a: any) => a.id);
              await fetchMemorandumsForAspirants(aspirantIds);
            }
          } else if (Array.isArray(aspirantsResponse.data)) {
            const aspirantsData = aspirantsResponse.data;
            setAspirants(aspirantsData);
            console.log(`✅ Found ${aspirantsData.length} aspirants`);
            
            if (aspirantsData.length > 0) {
              const aspirantIds = aspirantsData.map((a: any) => a.id);
              await fetchMemorandumsForAspirants(aspirantIds);
            }
          } else if (aspirantsResponse.data.data && Array.isArray(aspirantsResponse.data.data)) {
            const aspirantsData = aspirantsResponse.data.data;
            setAspirants(aspirantsData);
            console.log(`✅ Found ${aspirantsData.length} aspirants`);
            
            if (aspirantsData.length > 0) {
              const aspirantIds = aspirantsData.map((a: any) => a.id);
              await fetchMemorandumsForAspirants(aspirantIds);
            }
          } else {
            console.log('⚠️ Unexpected aspirants response format:', aspirantsResponse.data);
            setAspirants([]);
          }
        } else {
          setAspirants([]);
        }
      } catch (aspirantError) {
        console.error('❌ Error fetching aspirants:', aspirantError);
        setAspirants([]);
      }

      // Fetch public manifestos for citizen's ward
      await fetchPublicManifestos(citizenWardId);

    } catch (error: any) {
      console.error('❌ Error fetching community data:', error);
      setError('Failed to load community data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMemorandumsForAspirants = async (aspirantIds: number[]) => {
    try {
      const allMemorandums: Memorandum[] = [];
      
      // Fetch memorandums for each aspirant
      for (const aspirantId of aspirantIds) {
        try {
          const response = await apiService.getMemorandumsByAspirant(aspirantId);
          if (response.data.success && response.data.data) {
            allMemorandums.push(...response.data.data);
          }
        } catch (error) {
          console.error(`Error fetching memorandums for aspirant ${aspirantId}:`, error);
          // Continue with next aspirant
        }
      }
      
      setMemorandums(allMemorandums);
      console.log(`✅ Found ${allMemorandums.length} memorandums total`);
    } catch (error) {
      console.error('Error fetching memorandums:', error);
      // Don't set error here - memorandums are secondary content
    }
  };

  const fetchPublicManifestos = async (wardId: number) => {
    try {
      console.log('📜 Fetching public manifestos for ward:', wardId);
      const response = await apiService.getPublicManifestosByWard(wardId);
      
      // Handle different response structures safely
      if (response && response.data) {
        // Case 1: Standard { success: true, data: [...] } format
        if (response.data.success && Array.isArray(response.data.data)) {
          setManifestos(response.data.data);
          console.log(`✅ Found ${response.data.data.length} public manifestos`);
        } 
        // Case 2: Direct array response
        else if (Array.isArray(response.data)) {
          setManifestos(response.data);
          console.log(`✅ Found ${response.data.length} public manifestos`);
        } 
        // Case 3: Nested data property
        else if (response.data.data && Array.isArray(response.data.data)) {
          setManifestos(response.data.data);
          console.log(`✅ Found ${response.data.data.length} public manifestos`);
        } 
        // Case 4: Empty or unexpected format
        else {
          console.log('Unexpected response format:', response.data);
          setManifestos([]);
        }
      } else {
        console.log('No manifestos found or empty response');
        setManifestos([]);
      }
    } catch (error) {
      console.error('Error fetching manifestos:', error);
      // Don't show error to user - manifestos are optional content
      setManifestos([]);
    }
  };

  const handleDownloadDocument = async (url: string, filename: string) => {
    try {
      if (url) {
        window.open(url, '_blank');
        toast.success('Document opened in new tab');
      } else {
        toast.error('No document available');
      }
    } catch (error) {
      toast.error('Failed to open document');
    }
  };

  const handleViewAspirant = (aspirantId: number) => {
    navigate(`/citizen/aspirant/${aspirantId}`);
  };

  const handleShareContent = (type: string, id: number, title: string) => {
    const shareUrl = `${window.location.origin}/citizen/community/${type}/${id}`;
    const shareText = `Check out this ${type} from our community: ${title}`;
    
    if (navigator.share) {
      navigator.share({
        title: `${title} - Community Update`,
        text: shareText,
        url: shareUrl,
      }).catch(() => {
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleGiveFeedback = (feedbackLink: string | undefined) => {
    if (feedbackLink) {
      window.open(feedbackLink, '_blank');
    } else {
      toast.error('No feedback link available for this content');
    }
  };

  // Helper function to get display name from type object
  const getTypeDisplayName = (typeObj: any): string => {
    if (!typeObj) return 'Unknown';
    if (typeof typeObj === 'string') return typeObj;
    if (typeObj.displayName) return typeObj.displayName;
    if (typeObj.name) return typeObj.name;
    return 'Unknown';
  };

  // Helper function to get ward display name
  const getWardDisplayName = (): string => {
    if (!typedCitizen) return 'Not set';
    return typedCitizen.wardName || `Ward ${typedCitizen.wardId}` || 'Not set';
  };

  // Filter campaigns based on search and filters
  const filteredCampaigns = campaigns.filter(campaign => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const nameMatch = campaign.campaignName?.toLowerCase().includes(searchLower) || false;
      const descMatch = campaign.description?.toLowerCase().includes(searchLower) || false;
      if (!nameMatch && !descMatch) {
        return false;
      }
    }
    
    // Category filter
    if (categoryFilter !== 'all') {
      const campaignType = getTypeDisplayName(campaign.type);
      if (campaignType !== categoryFilter) {
        return false;
      }
    }
    
    // Date filter
    if (dateFilter !== 'all') {
      const campaignDate = new Date(campaign.publishedAt || campaign.createdAt);
      const now = new Date();
      const daysDiff = Math.floor((now.getTime() - campaignDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (dateFilter === 'today' && daysDiff > 0) return false;
      if (dateFilter === 'week' && daysDiff > 7) return false;
      if (dateFilter === 'month' && daysDiff > 30) return false;
    }
    
    return true;
  });

  // Filter manifestos
  const filteredManifestos = manifestos.filter(manifesto => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const titleMatch = manifesto.title?.toLowerCase().includes(searchLower) || false;
      const aspirantMatch = manifesto.aspirantName?.toLowerCase().includes(searchLower) || false;
      const summaryMatch = manifesto.summary?.toLowerCase().includes(searchLower) || false;
      if (!titleMatch && !aspirantMatch && !summaryMatch) {
        return false;
      }
    }
    
    // Category filter (source)
    if (categoryFilter !== 'all' && manifesto.source !== categoryFilter) {
      return false;
    }
    
    // Date filter
    if (dateFilter !== 'all') {
      const manifestoDate = new Date(manifesto.publishedAt || manifesto.createdAt);
      const now = new Date();
      const daysDiff = Math.floor((now.getTime() - manifestoDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (dateFilter === 'week' && daysDiff > 7) return false;
      if (dateFilter === 'month' && daysDiff > 30) return false;
      if (dateFilter === 'year' && daysDiff > 365) return false;
    }
    
    return true;
  });

  // Filter memorandums
  const filteredMemorandums = memorandums.filter(memo => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const subjectMatch = memo.subject?.toLowerCase().includes(searchLower) || false;
      const contentMatch = memo.content?.toLowerCase().includes(searchLower) || false;
      return subjectMatch || contentMatch;
    }
    return true;
  });

  // Filter aspirants
  const filteredAspirants = aspirants.filter(aspirant => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const nameMatch = aspirant.name?.toLowerCase().includes(searchLower) || false;
      const partyMatch = aspirant.party?.toLowerCase().includes(searchLower) || false;
      return nameMatch || partyMatch;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0b2045] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading community updates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="w-16 h-16 bg-[#fefce8] rounded-full flex items-center justify-center mx-auto mb-4">
          <MapPin className="text-[#ca8a04]" size={32} />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Ward Information Required</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={() => navigate('/citizen/profile')}
          className="px-6 py-3 bg-[#0b2045] text-white rounded-lg hover:bg-[#152c53] font-medium"
        >
          Update Profile
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0b2045] to-[#1a3d6e] rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Community Hub</h1>
            <p className="text-[#a8d59b]">
              Stay updated with campaigns, manifestos, and memorandums from aspirants in your ward
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
              <span className="text-sm">Your Ward: </span>
              <span className="font-semibold">{getWardDisplayName()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Campaigns</p>
              <p className="text-2xl font-bold text-gray-900">{campaigns.length}</p>
            </div>
            <div className="p-2 bg-[#0b2045]/10 rounded-lg">
              <Megaphone className="text-[#0b2045]" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Public Manifestos</p>
              <p className="text-2xl font-bold text-gray-900">{manifestos.length}</p>
            </div>
            <div className="p-2 bg-[#3c8d2e]/10 rounded-lg">
              <Award className="text-[#3c8d2e]" size={24} />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Policy documents & vision statements</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Aspirants in Ward</p>
              <p className="text-2xl font-bold text-gray-900">{aspirants.length}</p>
            </div>
            <div className="p-2 bg-[#3c8d2e]/10 rounded-lg">
              <Users className="text-[#3c8d2e]" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Official Memorandums</p>
              <p className="text-2xl font-bold text-gray-900">{memorandums.length}</p>
            </div>
            <div className="p-2 bg-[#0b2045]/10 rounded-lg">
              <FileText className="text-[#0b2045]" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs and Filters */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-[#0b2045]/10">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`flex-1 px-4 py-4 text-center font-medium ${
                activeTab === 'campaigns'
                  ? 'text-[#0b2045] border-b-2 border-[#3c8d2e]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-[#f4f7f6]'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Megaphone size={18} />
                Campaigns ({campaigns.length})
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab('manifestos')}
              className={`flex-1 px-4 py-4 text-center font-medium ${
                activeTab === 'manifestos'
                  ? 'text-[#0b2045] border-b-2 border-[#3c8d2e]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-[#f4f7f6]'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Award size={18} />
                Manifestos ({manifestos.length})
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab('memorandums')}
              className={`flex-1 px-4 py-4 text-center font-medium ${
                activeTab === 'memorandums'
                  ? 'text-[#0b2045] border-b-2 border-[#3c8d2e]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-[#f4f7f6]'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <FileText size={18} />
                Memorandums ({memorandums.length})
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab('aspirants')}
              className={`flex-1 px-4 py-4 text-center font-medium ${
                activeTab === 'aspirants'
                  ? 'text-[#0b2045] border-b-2 border-[#3c8d2e]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-[#f4f7f6]'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Users size={18} />
                Aspirants ({aspirants.length})
              </div>
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="search"
                  placeholder={`Search ${activeTab}...`}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              {activeTab === 'campaigns' && (
                <>
                  <select
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="all">All Types</option>
                    <option value="MANIFESTO">Manifesto</option>
                    <option value="STRATEGY">Strategy</option>
                    <option value="UPDATE">Update</option>
                    <option value="EVENT">Event</option>
                  </select>
                  <select
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                  </select>
                </>
              )}

              {activeTab === 'manifestos' && (
                <>
                  <select
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="all">All Sources</option>
                    <option value="CITIZEN_REPORTS">Citizen Reports</option>
                    <option value="CUSTOM">Custom</option>
                    <option value="TEMPLATE">Template</option>
                    <option value="HYBRID">Hybrid</option>
                  </select>
                  <select
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                  >
                    <option value="all">All Time</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                  </select>
                </>
              )}

              <button
                onClick={() => {
                  setSearchTerm('');
                  setCategoryFilter('all');
                  setDateFilter('all');
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-[#f4f7f6] transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6">
          {/* Campaigns Tab */}
          {activeTab === 'campaigns' && (
            <div className="space-y-6">
              {filteredCampaigns.length === 0 ? (
                <div className="text-center py-12">
                  <Megaphone className="mx-auto text-gray-400 mb-4" size={48} />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h3>
                  <p className="text-gray-600">
                    {searchTerm || categoryFilter !== 'all' || dateFilter !== 'all'
                      ? 'No campaigns match your filters'
                      : 'Aspirants in your ward haven\'t published any campaigns yet.'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredCampaigns.map((campaign) => (
                    <div key={campaign.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow hover:border-[#3c8d2e]/30">
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#0b2045]/10 text-[#0b2045] mb-2">
                              {getTypeDisplayName(campaign.type)}
                            </span>
                            <h3 className="text-lg font-semibold text-[#0b2045]">{campaign.campaignName}</h3>
                            <p className="text-sm text-gray-600 mt-1">By {campaign.aspirantName}</p>
                          </div>
                          {campaign.imageUrl && (
                            <img 
                              src={campaign.imageUrl} 
                              alt={campaign.campaignName}
                              className="w-16 h-16 object-cover rounded-lg"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          )}
                        </div>
                        
                        <p className="text-gray-700 mb-4 line-clamp-3">{campaign.description || campaign.summary || campaign.content.substring(0, 150)}...</p>
                        
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center">
                              <Calendar size={14} className="mr-1" />
                              {new Date(campaign.publishedAt || campaign.createdAt).toLocaleDateString()}
                            </span>
                            {campaign.smsRecipients && campaign.smsRecipients > 0 && (
                              <span className="flex items-center">
                                <MessageSquare size={14} className="mr-1" />
                                {campaign.smsRecipients.toLocaleString()} reached
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => handleShareContent('campaign', campaign.id, campaign.campaignName)}
                            className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-[#0b2045] hover:bg-[#f4f7f6] rounded-lg transition-colors"
                          >
                            <Share2 size={14} />
                            Share
                          </button>
                          {campaign.documentUrl && (
                            <button
                              onClick={() => handleDownloadDocument(campaign.documentUrl!, `${campaign.campaignName}.pdf`)}
                              className="flex items-center gap-1 px-3 py-2 text-sm text-[#3c8d2e] hover:text-[#2d7022] hover:bg-[#e8f1ec] rounded-lg transition-colors"
                            >
                              <Download size={14} />
                              View Document
                            </button>
                          )}
                          {campaign.feedbackLink && (
                            <button
                              onClick={() => handleGiveFeedback(campaign.feedbackLink)}
                              className="flex items-center gap-1 px-3 py-2 text-sm text-[#3c8d2e] hover:text-[#2d7022] hover:bg-[#e8f1ec] rounded-lg transition-colors"
                            >
                              <MessageSquare size={14} />
                              Give Feedback
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Manifestos Tab */}
          {activeTab === 'manifestos' && (
            <div className="space-y-6">
              {filteredManifestos.length === 0 ? (
                <div className="text-center py-12">
                  <Award className="mx-auto text-gray-400 mb-4" size={48} />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No public manifestos yet</h3>
                  <p className="text-gray-600">
                    {searchTerm || categoryFilter !== 'all' || dateFilter !== 'all'
                      ? 'No manifestos match your filters'
                      : 'Aspirants in your ward haven\'t published any public manifestos yet.'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  {filteredManifestos.map((manifesto) => (
                    <ManifestoCard
                      key={manifesto.id}
                      manifesto={manifesto}
                      onShare={(m) => {
                        const shareUrl = `${window.location.origin}/citizen/manifesto/${m.aspirantId}/${m.id}`;
                        navigator.clipboard.writeText(shareUrl);
                        toast.success('Link copied to clipboard!');
                      }}
                      onDownload={(url, filename) => {
                        window.open(url, '_blank');
                      }}
                      onFeedback={(link) => {
                        window.open(link, '_blank');
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Memorandums Tab */}
          {activeTab === 'memorandums' && (
            <div className="space-y-4">
              {filteredMemorandums.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto text-gray-400 mb-4" size={48} />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No memorandums yet</h3>
                  <p className="text-gray-600">
                    {searchTerm
                      ? 'No memorandums match your search'
                      : 'Aspirants in your ward haven\'t issued any memorandums yet.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredMemorandums.map((memo) => (
                    <div key={memo.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:bg-[#f4f7f6] transition-colors hover:border-[#3c8d2e]/30">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#0b2045]/10 text-[#0b2045] mb-2">
                            {getTypeDisplayName(memo.type)}
                          </span>
                          <h3 className="text-lg font-semibold text-[#0b2045]">{memo.subject}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {memo.aspirantName} → {memo.recipientDepartment}
                          </p>
                        </div>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {memo.memorandumNumber}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 mb-4 line-clamp-3">{memo.content}</p>
                      
                      {memo.evidenceSummary && (
                        <div className="mb-4 p-3 bg-[#0b2045]/10 rounded-lg">
                          <p className="text-sm font-medium text-[#0b2045] mb-1">Evidence Summary</p>
                          <p className="text-sm text-[#0b2045]/80">{memo.evidenceSummary}</p>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
                        <span className="flex items-center">
                          <Calendar size={14} className="mr-1" />
                          {new Date(memo.generatedAt).toLocaleDateString()}
                        </span>
                        {memo.estimatedCost && (
                          <span className="flex items-center">
                            <TrendingUp size={14} className="mr-1" />
                            KES {memo.estimatedCost.toLocaleString()}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDownloadDocument(memo.downloadUrl!, `${memo.memorandumNumber}.txt`)}
                            className="flex items-center gap-1 px-3 py-2 text-sm text-[#3c8d2e] hover:text-[#2d7022] hover:bg-[#e8f1ec] rounded-lg transition-colors"
                          >
                            <Download size={14} />
                            Download
                          </button>
                          <button
                            onClick={() => handleShareContent('memorandum', memo.id, memo.subject)}
                            className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-[#0b2045] hover:bg-[#f4f7f6] rounded-lg transition-colors"
                          >
                            <Share2 size={14} />
                            Share
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Aspirants Tab */}
          {activeTab === 'aspirants' && (
            <div className="space-y-6">
              {filteredAspirants.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="mx-auto text-gray-400 mb-4" size={48} />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No aspirants found</h3>
                  <p className="text-gray-600">
                    {searchTerm
                      ? 'No aspirants match your search'
                      : 'No aspirants are currently registered in your ward.'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredAspirants.map((aspirant) => {
                    // Count campaigns and memorandums for this aspirant
                    const campaignCount = campaigns.filter(c => c.aspirantId === aspirant.id).length;
                    const memorandumCount = memorandums.filter(m => m.aspirantId === aspirant.id).length;
                    const manifestoCount = manifestos.filter(m => m.aspirantId === aspirant.id).length;
                    
                    return (
                      <div 
                        key={aspirant.id} 
                        className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow hover:border-[#3c8d2e]/30 cursor-pointer"
                        onClick={() => handleViewAspirant(aspirant.id)}
                      >
                        <div className="p-6">
                          <div className="flex items-start gap-4 mb-4">
                            <div className="flex-shrink-0">
                              <div className="w-16 h-16 bg-gradient-to-br from-[#0b2045] to-[#3c8d2e] rounded-full flex items-center justify-center text-white font-bold text-xl">
                                {aspirant.name?.charAt(0) || 'A'}
                              </div>
                            </div>
                            <div className="flex-1">
                              <h3 className="font-bold text-[#0b2045]">{aspirant.name}</h3>
                              <p className="text-sm text-gray-600">{getTypeDisplayName(aspirant.position)}</p>
                              {aspirant.party && (
                                <span className="inline-block mt-1 px-2 py-1 bg-[#f4f7f6] text-[#0b2045] text-xs rounded">
                                  {aspirant.party}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-2 mb-4">
                            <div className="text-center p-2 bg-[#0b2045]/10 rounded-lg">
                              <div className="text-lg font-bold text-[#0b2045]">{campaignCount}</div>
                              <div className="text-xs text-[#0b2045]/80">Campaigns</div>
                            </div>
                            <div className="text-center p-2 bg-[#3c8d2e]/10 rounded-lg">
                              <div className="text-lg font-bold text-[#3c8d2e]">{manifestoCount}</div>
                              <div className="text-xs text-[#3c8d2e]/80">Manifestos</div>
                            </div>
                            <div className="text-center p-2 bg-[#0b2045]/5 rounded-lg">
                              <div className="text-lg font-bold text-[#0b2045]">{memorandumCount}</div>
                              <div className="text-xs text-[#0b2045]/80">Memorandums</div>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex items-center text-sm text-gray-600">
                              <MapPin size={14} className="mr-2 text-gray-400" />
                              <span>{aspirant.constituencyName || aspirant.constituencyId || 'N/A'}</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <Calendar size={14} className="mr-2 text-gray-400" />
                              <span>Joined {aspirant.createdAt ? new Date(aspirant.createdAt).toLocaleDateString() : 'N/A'}</span>
                            </div>
                          </div>
                          
                          <button className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-[#0b2045] to-[#1a3d6e] text-white rounded-lg hover:opacity-90 transition-opacity">
                            View Profile
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info Section */}
      <div className="bg-gradient-to-r from-[#f4f7f6] to-[#e8f1ec] border border-[#0b2045]/10 rounded-xl p-6">
        <div className="flex items-start">
          <div className="p-2 bg-[#3c8d2e]/10 rounded-lg mr-4">
            <Award className="text-[#3c8d2e]" size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[#0b2045] mb-2">About Community Hub</h3>
            <p className="text-[#0b2045]/80">
              This hub shows content specifically from aspirants in your ward. You'll see:
            </p>
            <ul className="mt-2 space-y-1 text-[#0b2045]/70">
              <li className="flex items-center">
                <Target size={14} className="mr-2" />
                <span>Campaign manifestos and updates from your ward's aspirants</span>
              </li>
              <li className="flex items-center">
                <Award size={14} className="mr-2" />
                <span>Public manifestos with vision, mission, and policy commitments</span>
              </li>
              <li className="flex items-center">
                <FileText size={14} className="mr-2" />
                <span>Official memorandums sent to government departments</span>
              </li>
              <li className="flex items-center">
                <Users size={14} className="mr-2" />
                <span>Profiles of aspirants running in your area</span>
              </li>
            </ul>
            <p className="mt-3 text-sm text-[#3c8d2e]">
              To see content from other wards, update your ward information in your profile.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Community;